import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
  AppState,
  User,
  Marketer,
  AuthTokens
} from '../types';
import apiService from '../services/api';
import pollingService from '../services/polling';

// Actions
type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_MARKETER'; payload: Marketer }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'SET_TOKENS'; payload: AuthTokens }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_MARKETER_AVAILABILITY'; payload: boolean };

// Initial state
const initialState: AppState = {
  user: undefined,
  marketer: undefined,
  isAuthenticated: false,
  tokens: undefined,
  isLoading: true,
  error: undefined,
};

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: undefined };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_MARKETER':
      return { ...state, marketer: action.payload };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'SET_TOKENS':
      return { ...state, tokens: action.payload };
    case 'UPDATE_MARKETER_AVAILABILITY':
      return {
        ...state,
        marketer: state.marketer ? {
          ...state.marketer,
          is_available: action.payload
        } : undefined
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    default:
      return state;
  }
};

// Context type
interface AppContextType {
  state: AppState;
  loginWithPin: (phoneNumber: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  loadUserProfile: () => Promise<void>;
  loadMarketerProfile: () => Promise<void>;
  updateMarketerAvailability: (isAvailable: boolean) => Promise<void>;
  clearError: () => void;
}

// Create context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize app on mount
  useEffect(() => {
    initializeApp();
  }, []);

  // Initialize Polling when authenticated
  useEffect(() => {
    if (state.isAuthenticated && state.marketer) {
      initializePolling();
    } else {
      pollingService.stop();
    }

    // Cleanup on unmount
    return () => {
      pollingService.stop();
    };
  }, [state.isAuthenticated, state.marketer]);

  const initializeApp = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Check if user is authenticated
      if (apiService.isAuthenticated()) {
        const tokens = apiService.getAuthTokens();
        if (tokens) {
          dispatch({ type: 'SET_TOKENS', payload: tokens });
          dispatch({ type: 'SET_AUTHENTICATED', payload: true });

          // Load user and marketer profiles
          await loadUserProfile();
          await loadMarketerProfile();
        }
      }
    } catch (error: any) {
      console.error('Failed to initialize app:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to initialize application' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const initializePolling = () => {
    try {
      pollingService.start({
        onNewOrders: (orders: any[]) => {
          orders.forEach(order => {
            toast.success(`New order assigned: #${order.orderId}`, {
              duration: 5000,
              position: 'top-right',
            });
          });
          // Trigger data refresh in components if needed
          window.dispatchEvent(new CustomEvent('newOrders', { detail: orders }));
        },

        onOrderUpdate: (updates: any[]) => {
          updates.forEach(update => {
            toast(`Order #${update.orderId} status updated to ${update.status}`, {
              duration: 3000,
              position: 'top-right',
            });
          });
          // Trigger data refresh in components if needed
          window.dispatchEvent(new CustomEvent('orderUpdates', { detail: updates }));
        },

        onStatusUpdate: (status: any) => {
          // Update marketer availability in state
          if (state.marketer && status.isAvailable !== state.marketer.is_available) {
            dispatch({ type: 'UPDATE_MARKETER_AVAILABILITY', payload: status.isAvailable });
          }
        },

        onNotification: (count: number) => {
          if (count > 0) {
            toast(`You have ${count} new notification${count > 1 ? 's' : ''}`, {
              duration: 4000,
              position: 'top-right',
            });
          }
        },

        onError: (error: Error) => {
          console.error('Polling error:', error);
        },
      }, 5000); // Poll every 5 seconds

      console.log('Polling started');
    } catch (error: any) {
      console.error('Failed to initialize polling:', error);
    }
  };

  const loginWithPin = async (phoneNumber: string, pin: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const loginData = await apiService.loginWithPin(phoneNumber, pin);

      const tokens = {
        access: loginData.access,
        refresh: loginData.refresh,
      };

      dispatch({ type: 'SET_TOKENS', payload: tokens });
      dispatch({ type: 'SET_AUTHENTICATED', payload: true });

      // Set marketer data directly from login response
      dispatch({ type: 'SET_MARKETER', payload: loginData.marketer });

      // Also load user profile for completeness
      try {
        await loadUserProfile();
      } catch (userError: any) {
        console.error('Failed to load user profile:', userError);
      }

      toast.success('Successfully logged in!');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await apiService.logout();
      pollingService.stop();
      dispatch({ type: 'LOGOUT' });
      toast.success('Successfully logged out!');
    } catch (error: any) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      dispatch({ type: 'LOGOUT' });
    }
  };

  const loadUserProfile = async (): Promise<void> => {
    try {
      const user = await apiService.getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error: any) {
      console.error('Failed to load user profile:', error);
      throw error;
    }
  };

  const loadMarketerProfile = async (): Promise<void> => {
    try {
      const marketer = await apiService.getMarketerProfile();
      dispatch({ type: 'SET_MARKETER', payload: marketer });
    } catch (error: any) {
      // If marketer profile doesn't exist, that's okay
      if (error.response?.status !== 404) {
        console.error('Failed to load marketer profile:', error);
        throw error;
      }
    }
  };

  const updateMarketerAvailability = async (isAvailable: boolean): Promise<void> => {
    try {
      dispatch({ type: 'UPDATE_MARKETER_AVAILABILITY', payload: isAvailable });

      // Update via API
      if (state.marketer) {
        await apiService.updateMarketerProfile({ is_available: isAvailable });
      }

      // Trigger immediate poll to get updated status
      await pollingService.pollNow();

      toast.success(`Availability ${isAvailable ? 'enabled' : 'disabled'}`);
    } catch (error: any) {
      // Revert the optimistic update
      dispatch({ type: 'UPDATE_MARKETER_AVAILABILITY', payload: !isAvailable });

      const errorMessage = error.response?.data?.message || error.message || 'Failed to update availability';
      toast.error(errorMessage);
      throw error;
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AppContextType = {
    state,
    loginWithPin,
    logout,
    loadUserProfile,
    loadMarketerProfile,
    updateMarketerAvailability,
    clearError,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;